import { Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import Swal2 from 'sweetalert2';
import { RequestService } from '../request/request.service';

export type GoogleAuthenticationPurpose = 'drive' | 'youtube';

@Injectable({ providedIn: 'root' })
export class GoogleAccountAuthService {
  constructor(private request: RequestService) {}

  async requireAuthentication(purpose: GoogleAuthenticationPurpose): Promise<any | null> {
    try {
      const authentication = this.parseResponse(
        await this.request.createRequestPost('getAuthYouTubeToken', {})
      );

      if (authentication?.success) {
        return authentication;
      }

      const destination = purpose === 'drive' ? 'Google Drive' : 'YouTube';
      const result = await Swal2.fire({
        title: 'Cuenta de Google no conectada',
        text: `Necesitas iniciar sesión con Google para acceder a ${destination}. ¿Quieres iniciar sesión ahora?`,
        icon: 'info',
        position: 'top',
        toast: true,
        showCancelButton: true,
        confirmButtonText: 'Iniciar sesión con Google',
        cancelButtonText: 'Ahora no',
        confirmButtonColor: '#4285f4',
        cancelButtonColor: '#596275',
        color: '#ffffff',
        background: '#010f4e',
      });

      if (result.isConfirmed) {
        await this.openGoogleAuthentication(purpose);
      }

      return null;
    } catch (error) {
      console.error('Unable to verify Google authentication:', error);
      await Swal2.fire({
        title: 'No pudimos verificar tu cuenta de Google',
        text: 'Inténtalo nuevamente en unos momentos.',
        icon: 'error',
        position: 'top',
        toast: true,
        confirmButtonText: 'Entendido',
        color: '#ffffff',
        background: '#010f4e',
      });
      return null;
    }
  }

  async openGoogleAuthentication(purpose: GoogleAuthenticationPurpose): Promise<void> {
    const response = await this.request.createRequestPost(
      'redirectToYoutubeEntanglement',
      purpose === 'drive' ? { drive: true } : {}
    );
    const parsed = this.parseResponse(response);
    const loginUrl = typeof parsed === 'string' ? parsed : parsed?.url;

    if (!loginUrl) {
      throw new Error('Google authentication URL was not returned.');
    }

    if (Capacitor.getPlatform() === 'android') {
      window.open(loginUrl, '_blank', 'location=yes,toolbar=yes');
      return;
    }

    await Browser.open({ url: loginUrl });
  }

  private parseResponse(response: any): any {
    if (typeof response !== 'string') {
      return response;
    }

    const trimmed = response.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[') && !trimmed.startsWith('"')) {
      return response;
    }

    try {
      return JSON.parse(trimmed);
    } catch {
      return response;
    }
  }
}
